import { tierPlanAPI } from "./api";

const MIN_VISIBLE_DAYS = 15;
const EXTRA_PAST_DAYS = 15;
const EXTRA_FUTURE_DAYS = 45;
// Количество лет вперед для отображения календаря (для бесконечного скролла)
const YEARS_AHEAD = 10;
const MONTH_NAMES = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

export const dateToIso = (date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const isoToDate = (isoString) => {
  const [year, month, day] = isoString.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

export const addDaysIso = (isoString, days) => {
  const date = isoToDate(isoString);
  date.setUTCDate(date.getUTCDate() + days);
  return dateToIso(date);
};

const diffInDays = (startIso, endIso) => {
  const start = isoToDate(startIso);
  const end = isoToDate(endIso);
  return Math.floor((end - start) / (24 * 60 * 60 * 1000));
};

export const normalizeDateString = (dateStr) => {
  if (!dateStr) return null;

  if (dateStr instanceof Date) {
    if (isNaN(dateStr.getTime())) return null;
    return dateToIso(dateStr);
  }

  const trimmed = String(dateStr).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.includes("T")) {
    const date = new Date(trimmed);
    if (isNaN(date.getTime())) return null;
    return dateToIso(date);
  }

  if (trimmed.includes(".")) {
    const parts = trimmed.split(".");
    if (parts.length === 3) {
      const [day, month, year] = parts.map((part) => parseInt(part, 10));
      if (
        !Number.isNaN(day) &&
        !Number.isNaN(month) &&
        !Number.isNaN(year) &&
        day > 0 &&
        day <= 31 &&
        month > 0 &&
        month <= 12
      ) {
        return `${String(year).padStart(4, "0")}-${String(month).padStart(
          2,
          "0"
        )}-${String(day).padStart(2, "0")}`;
      }
    }
  }

  const date = new Date(trimmed);
  if (isNaN(date.getTime())) return null;
  return dateToIso(date);
};

export const parseDateInfo = (isoString) => {
  const normalized = normalizeDateString(isoString);
  if (!normalized) return null;

  const [yearStr, monthStr, dayStr] = normalized.split("-");
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);

  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }

  return {
    original: normalized,
    day,
    month,
    year,
    monthName: MONTH_NAMES[month - 1],
    sortKey: normalized,
  };
};

const buildDateSequence = (startIso, endIso) => {
  const dates = [];
  let current = startIso;

  while (current <= endIso) {
    const dateInfo = parseDateInfo(current);
    if (dateInfo) {
      dates.push(dateInfo);
    }
    current = addDaysIso(current, 1);
  }

  return dates;
};

export const buildPlannerDates = (normalizedDates, firstDateWithData = null) => {
  const todayIso = dateToIso(new Date());

  // Если указана первая дата с данными, используем её как стартовую точку
  let startDate = firstDateWithData;
  
  if (!startDate && normalizedDates && normalizedDates.length > 0) {
    const sortedDates = [...new Set(normalizedDates)].sort((a, b) =>
      a.localeCompare(b)
    );
    startDate = sortedDates[0];
  }

  if (!startDate) {
    // Если нет данных, используем текущую дату
    const start = addDaysIso(todayIso, -Math.floor(MIN_VISIBLE_DAYS / 2));
    // Создаем большой диапазон для бесконечного скролла
    const end = addDaysIso(start, YEARS_AHEAD * 365);
    return buildDateSequence(start, end);
  }

  // Начинаем с первого дня месяца, в котором находится первая дата с данными
  let start = startDate;
  if (startDate) {
    // Получаем первый день месяца для startDate
    const date = isoToDate(startDate);
    const firstDayOfMonth = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
    start = dateToIso(firstDayOfMonth);
  }
  
  let end;
  
  if (normalizedDates && normalizedDates.length > 0) {
    const sortedDates = [...new Set(normalizedDates)].sort((a, b) =>
      a.localeCompare(b)
    );
    const lastDateWithData = sortedDates[sortedDates.length - 1];
    // Создаем большой диапазон: от первого дня месяца минимальной даты до последней даты + много лет вперед
    // Это позволяет скроллить бесконечно вперед и переключаться между месяцами на годы вперед
    end = addDaysIso(lastDateWithData, YEARS_AHEAD * 365);
  } else {
    // Если нет данных, создаем большой диапазон от стартовой даты
    end = addDaysIso(start, YEARS_AHEAD * 365);
  }

  // Убираем проверку на минимальный span, так как теперь у нас большой диапазон
  return buildDateSequence(start, end);
};

export const groupDatesByMonth = (dates) => {
  const monthGroups = [];
  let currentKey = null;
  let currentGroup = null;

  dates.forEach((dateInfo) => {
    const monthKey = `${dateInfo.year}-${String(dateInfo.month).padStart(
      2,
      "0"
    )}`;
    if (currentKey !== monthKey || !currentGroup) {
      if (currentGroup) {
        monthGroups.push(currentGroup);
      }
      currentKey = monthKey;
      currentGroup = {
        month: dateInfo.month,
        monthName: dateInfo.monthName,
        year: dateInfo.year,
        dates: [],
      };
    }

    currentGroup.dates.push(dateInfo);
  });

  if (currentGroup) {
    monthGroups.push(currentGroup);
  }

  return monthGroups;
};

// Генерирует все месяцы в диапазоне от startDate до endDate
export const generateAllMonthGroups = (startDate, endDate) => {
  const monthGroups = [];
  const start = isoToDate(startDate);
  const end = isoToDate(endDate);
  
  let currentDate = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const endMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
  
  while (currentDate <= endMonth) {
    const year = currentDate.getUTCFullYear();
    const month = currentDate.getUTCMonth() + 1;
    const monthName = MONTH_NAMES[month - 1];
    
    // Генерируем все даты для этого месяца
    const daysInMonth = new Date(year, month, 0).getDate();
    const dates = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dateInfo = parseDateInfo(dateStr);
      if (dateInfo) {
        dates.push(dateInfo);
      }
    }
    
    monthGroups.push({
      month,
      monthName,
      year,
      dates,
    });
    
    // Переходим к следующему месяцу
    currentDate = new Date(Date.UTC(year, month, 1));
  }
  
  return monthGroups;
};

export const getResidents = async () => {
  try {
    const res = await tierPlanAPI.getResidents();
    return res?.data || [];
  } catch (e) {
    return [];
  }
};

export const getBlocks = async (resident_id) => {
  try {
    if (!resident_id) return [];
    const res = await tierPlanAPI.getBlocks(resident_id);
    return res?.data || [];
  } catch (e) {
    return [];
  }
};

export const getWorkGroups = async (block_id) => {
  try {
    if (!block_id) return [];
    const res = await tierPlanAPI.getWorkGroups(block_id);
    return res?.data || [];
  } catch (e) {
    return [];
  }
};

export const getWorkGroupLevelNum = async (work_group_id) => {
  try {
    if (!work_group_id) return [];
    const res = await tierPlanAPI.getWorkGroupLevelNum(work_group_id);
    return res?.data || [];
  } catch (e) {
    return [];
  }
};

export const getWorkGroupLevels = async (work_group_id) => {
  try {
    if (!work_group_id) return [];
    const res = await tierPlanAPI.getWorkGroupLevels(work_group_id);
    return res?.data || [];
  } catch (e) {
    return [];
  }
};

export const getWorkTaskPlan = async (work_group_id, work_group_level_id = null) => {
  try {
    if (!work_group_id) {
      return { data: [] };
    }

    const res = await tierPlanAPI.getWorkTaskPlan(work_group_id, work_group_level_id);
    return res;
  } catch (e) {
    console.error('Error in getWorkTaskPlan:', e);
    return { data: [] };
  }
};

export const getWorkTaskPlanColumns = (dates) => {
  const columns = [
    {
      name: "task_name",
      label: "Название задачи",
      align: "left",
      field: "task_name",
      sortable: true,
    },
  ];

  dates.forEach((dateInfo) => {
    columns.push({
      name: dateInfo.original,
      label: dateInfo.day,
      align: "center",
      field: dateInfo.original,
    });
  });

  return columns;
};

export const updateWorkTaskPlan = async (data) => {
  try {
    const res = await tierPlanAPI.updateWorkTaskPlan(data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteWorkTaskPlan = async (data) => {
  try {
    const res = await tierPlanAPI.deleteWorkTaskPlan(data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const getLaborPlan = async (work_group_id) => {
  try {
    if (!work_group_id) {
      return { data: [] };
    }

    const res = await tierPlanAPI.getLaborPlan(work_group_id);
    return res;
  } catch (e) {
    console.error('Error in getLaborPlan:', e);
    return { data: [] };
  }
};

export const updateLaborPlan = async (data) => {
  try {
    const res = await tierPlanAPI.updateLaborPlan(data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteLaborPlan = async (data) => {
  try {
    const res = await tierPlanAPI.deleteLaborPlan(data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const undoWorkTaskPlan = async (data) => {
  try {
    const res = await tierPlanAPI.undoWorkTaskPlan(data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const copyWorkTaskPlanRange = async (data) => {
  try {
    const res = await tierPlanAPI.copyWorkTaskPlanRange(data);
    return res;
  } catch (e) {
    throw e;
  }
};

